import type { API } from 'chessboard/api/config/types';

interface CachedMethod {
  extensions: { [key: string]: API.APIFactory };
  original: Function;
}

/**
 * Create the extendAPI method.
 */
export function createExtendAPI(context: API.Context) {
  /**
   * Cached methods for reassignment when extensions of a particular
   * source are destroyed.
   */
  const cachedMethods: { [key: string]: CachedMethod } = {};

  /**
   * Extends the API by calling a factory function with the API context.
   */
  return function extendAPI(id: string, extensions: Record<string, API.APIFactory>): Function {
    const destroyOverrides: (Function | undefined)[] = Object.entries(extensions).map(
      ([key, fn]) => {
        if (!fn) {
          return;
        }

        if (!cachedMethods[key]) {
          cachedMethods[key] = {
            extensions: {},
            original: context.api[key],
          };
        }

        context.api[key] = fn(context);

        cachedMethods[key].extensions[id] = fn;

        return function destroyOverride() {
          delete cachedMethods[key].extensions[id];

          context.api[key] = cachedMethods[key].original;

          Object.keys(cachedMethods[key].extensions).map(
            (extensionId) =>
              (context.api[key] = cachedMethods[key].extensions[extensionId](context)),
          );
        };
      },
    );

    return function destroyOverride() {
      destroyOverrides.map((destroy) => destroy && destroy());
    };
  };
}
